import { h, render } from "preact";
import App from "./components/App/App";
import MvAddressSelectorSDKOptions, {
    MvOptionsErrors,
} from "./models/MvAddressSelectorSDKOptions";
import { validateOptions } from "services/setup";

export type { default as MvAddressSelectorSDKOptions } from "./models/MvAddressSelectorSDKOptions";
export type { default as MvSelectedAddress } from "./models/MvSelectedAddress";

export interface MvRenderProps {
    options: MvAddressSelectorSDKOptions;
    optionsError: MvOptionsErrors;
    el: Element | HTMLElement;
    merge?: Element;
}

export interface MvInitProps {
    options: MvAddressSelectorSDKOptions;
    element: any;
    setOptions(
        changedOptions: Partial<MvAddressSelectorSDKOptions>,
    ): MvAddressSelectorSDKOptions;
    tearDown(): void;
}

/**
 * Renders the MV AddressSelector component
 * @param  {Object} options: MvAddressSelectorSDKOptions configuration options
 * @param  {DOMElement} el:      target element where te component needs to be rendered
 * @param  {DOMElement} merge:   original element from first render (needed by preact for a rerender)
 * @return {DOMElement}          returns element from render
 */
const mvRender = ({ options, optionsError, el, merge }: MvRenderProps) =>
    render(<App options={options} optionsError={optionsError} />, el, merge);

const sdkConfigDefaults: MvAddressSelectorSDKOptions = {
    useModal: true,
    isModalOpen: true,
    shouldCloseOnOverlayClick: false,
    containerId: "mv-address-selector",
    apiBasePath: "",
    language: "en",
    messages: null,
    startRadius: 400,
    // maxRadius: 400,
    onError: (error: { message: string; type: string }) => {
        console.error(error.message);
    },
    onCancel: (data: any) => {
        console.info(data);
    },
    onComplete: (data: any) => {
        console.log(data);
    },
    onModalRequestClose: () => {
        console.log("request to close!");
    }, // do something
};

const withDefaultPricing = (
    options: MvAddressSelectorSDKOptions,
): MvAddressSelectorSDKOptions => {
    return {
        ...options,
        ...(options?.pricePerAddress === undefined &&
        options?.tieredPrices === undefined
            ? { pricePerAddress: 0.2 }
            : {}),
    };
};

export const init = (opts: MvAddressSelectorSDKOptions): MvInitProps => {
    const options = withDefaultPricing({
        ...sdkConfigDefaults,
        ...opts,
    });

    const optionsError = validateOptions(options);
    if (optionsError) {
        options?.onError?.({
            type: "invalid_options",
            message: JSON.stringify(optionsError),
        });
    }

    const containerEl: Element | HTMLElement = document.getElementById(
        options.containerId,
    )!;
    const element = mvRender({ options, optionsError, el: containerEl });

    return {
        options,
        element,
        /**
         * Merges the original options with the new ones
         * @param {Object} changedOptions the new options object
         */
        setOptions(changedOptions: Partial<MvAddressSelectorSDKOptions>) {
            this.options = { ...this.options, ...changedOptions };
            this.element = mvRender({
                options: this.options,
                optionsError,
                el: containerEl,
                merge: this.element,
            });
            return this.options;
        },
        tearDown() {
            render(null, containerEl, this.element);
        },
    };
};
